import { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, categories } from '@/mocks/comics';
import { generateWebPageSchema, generateBreadcrumbListSchema, generateComicSchema } from '@/utils/seo';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-800 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已被移除。</p>
            <Link
              to="/category/all"
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回漫画列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 5);

  const catName = categories.find((c) => c.slug === comic.category)?.name || comic.category;

  const pageSchema = generateWebPageSchema(
    `${comic.title} - 韩国漫画网`,
    comic.description.slice(0, 150),
    `https://www.example.com/article/${comic.id}`
  );
  const breadcrumbSchema = generateBreadcrumbListSchema([
    { name: '首页', url: 'https://www.example.com/' },
    { name: catName, url: `https://www.example.com/category/${comic.category}` },
    { name: comic.title, url: `https://www.example.com/article/${comic.id}` },
  ]);
  const comicSchema = generateComicSchema(comic);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(comicSchema) }} />
      <Navbar />
      <main className="flex-1">
        <div className="bg-background-100 py-6 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${comic.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                {categories.find((c) => c.slug === comic.category)?.name || comic.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{comic.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
                <div className="rounded-lg overflow-hidden aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">{comic.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                  <span className="text-foreground-300">|</span>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                    <span className="text-sm font-medium text-foreground-700">{comic.rating}</span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">{(comic.views / 10000).toFixed(1)}万 阅读</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">更新于 {comic.updateDate}</span>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                      {tag}
                    </span>
                  ))}
                  {comic.isHot && (
                    <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                      热门推荐
                    </span>
                  )}
                </div>

                <div className="mt-5 p-4 bg-background-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex items-center gap-3 mt-5 flex-wrap">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">
                    开始阅读 第{comic.chapters[0].number}话
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors duration-200 whitespace-nowrap">
                    <i className="ri-bookmark-line mr-1.5"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">章节列表</h2>
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between p-3.5 bg-background-100 rounded-lg hover:bg-primary-50 transition-colors duration-200 cursor-pointer"
                  >
                    <span className="text-sm text-foreground-700">{chapter.title}</span>
                    <span className="text-xs text-foreground-400">{chapter.date}</span>
                  </div>
                ))}
                <div className="text-center py-4">
                  <span className="text-sm text-foreground-400">共 {comic.chapters.length} 话</span>
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="lg:w-72">
                <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">相关推荐</h2>
                <div className="space-y-3">
                  {relatedComics.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="flex gap-3 p-3 bg-background-100 rounded-lg hover:bg-primary-50/50 transition-colors duration-200 group"
                    >
                      <div className="flex-shrink-0 w-14 h-20 rounded overflow-hidden">
                        <img
                          src={rel.cover}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                          {rel.title}
                        </h4>
                        <div className="flex items-center gap-1 mt-1">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                          <span className="text-xs text-foreground-500">{rel.rating}</span>
                        </div>
                        <p className="text-xs text-foreground-400 mt-1.5 line-clamp-2">{rel.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}